/**
 * Alarms & Comms (§7.1, view 7).
 *
 * The ISA-18.2 alarm list with acknowledge and shelve, the comms inbox with its action buttons,
 * and the event log. Every alarm carries its cause text and a link to the view where it can be
 * acted on — the "why is this alarm here" the brief asks for is the cause text itself, written to
 * say what is wrong and what to do about it.
 */

import { useSession } from '../../store/session';
import { useDispatch } from '../useEngine';
import { getSite } from '../../sites';
import type { ViewId } from '../../store/session';
import { duration } from '../format';

const PRIORITY_COLOUR: Record<string, string> = {
  emergency: 'var(--alarm)',
  high: 'var(--alarm)',
  medium: 'var(--warn)',
  low: 'var(--ink-dim)',
};

export function AlarmsComms(): JSX.Element {
  const snap = useSession((s) => s.snapshot);
  const site = getSite(useSession((s) => s.siteId));
  const setView = useSession((s) => s.setView);
  const requestConfirm = useSession((s) => s.requestConfirm);
  const dispatch = useDispatch();
  if (!snap) return <div style={{ padding: 24 }}>—</div>;

  return (
    <div style={{ padding: 'var(--s3)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(380px,1fr))', gap: 'var(--s3)' }}>
      <section className="panel" style={{ minWidth: 0 }}>
        <div className="label" style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
          <span>Alarms — {snap.alarms.length} active</span>
          <button style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => dispatch({ t: 'alarm-ack-all' })} data-testid="ack-all">
            Acknowledge all
          </button>
        </div>
        {snap.alarms.length === 0 && <div style={{ color: 'var(--ink-dim)' }}>No active alarms.</div>}
        <div style={{ display: 'grid', gap: 6 }}>
          {snap.alarms.map((a) => {
            const unacked = a.status === 'unacked-active';
            return (
              <div
                key={a.id}
                className={unacked && (a.priority === 'emergency' || a.priority === 'high') ? 'alarm-active' : undefined}
                style={{
                  background: 'var(--surface-2)',
                  borderLeft: `3px solid ${PRIORITY_COLOUR[a.priority] ?? 'var(--ink-dim)'}`,
                  padding: 'var(--s2)',
                }}
                data-testid="alarm-row"
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                  <span style={{ fontWeight: 600 }}>{a.title}</span>
                  <span
                    className="num"
                    style={{ fontSize: 11, textTransform: 'uppercase', color: PRIORITY_COLOUR[a.priority] }}
                  >
                    {a.priority} · {a.status.replace('-', ' ')}
                  </span>
                </div>
                <div style={{ color: 'var(--ink-dim)', margin: '3px 0 6px' }}>{a.cause}</div>
                <div className="num" style={{ fontSize: 11, color: 'var(--ink-dim)', marginBottom: 6 }}>
                  raised at minute {a.raisedAt.toLocaleString('en-US')}
                  {unacked && (a.priority === 'emergency' || a.priority === 'high') && a.unackedMinutes > 10 && (
                    <span style={{ color: 'var(--alarm)' }}>
                      {' '}
                      · unacknowledged {duration(a.unackedMinutes)} · $
                      {Math.round((a.unackedMinutes - 10) * 1000).toLocaleString('en-US')} accrued
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button disabled={!unacked} onClick={() => dispatch({ t: 'alarm-ack', id: a.id })}>
                    Acknowledge
                  </button>
                  <button onClick={() => setView(a.view as ViewId)}>Go to {a.view.replace('-', ' ')}</button>
                  <button onClick={() => dispatch({ t: 'alarm-shelve', id: a.id, minutes: 60 })} title="Shelving is time-limited and logged.">
                    Shelve 1 h
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="panel" style={{ minWidth: 0 }}>
        <div className="label" style={{ marginBottom: 8 }}>
          Comms
        </div>
        <div className="scroll" style={{ display: 'grid', gap: 8, maxHeight: 520 }}>
          {snap.comms.length === 0 && <div style={{ color: 'var(--ink-dim)' }}>Nothing yet.</div>}
          {[...snap.comms].reverse().map((c) => (
            <article key={c.id} style={{ background: 'var(--surface-2)', padding: 'var(--s2)', borderRadius: 3 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontWeight: 600 }}>{c.subject}</span>
                <span className="num" style={{ color: 'var(--ink-dim)', fontSize: 11 }}>
                  {c.from}
                </span>
              </div>
              <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{c.body}</div>
              {c.actions.length > 0 && (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  {c.actions.includes('request-tdg-waiver') && (
                    <button disabled={snap.tdgWaiverRequested} onClick={() => dispatch({ t: 'request-tdg-waiver' })}>
                      Request TDG waiver
                    </button>
                  )}
                  {c.actions.includes('issue-flood-warning') && (
                    <button disabled={snap.floodWarningAt !== null} onClick={() => dispatch({ t: 'issue-flood-warning' })}>
                      Issue flood warning
                    </button>
                  )}
                  {c.actions.includes('dispatch-crew') && (
                    <button onClick={() => dispatch({ t: 'dispatch-crew', target: 'spillway hoist' })}>Dispatch crew</button>
                  )}
                  {c.actions.includes('declare-eap') && (
                    <button
                      onClick={() =>
                        requestConfirm({
                          label: 'Declare EAP level 1',
                          detail: 'Opens the incident log and notifies dam safety.',
                          confirm: () => dispatch({ t: 'declare-eap', level: 1 }),
                        })
                      }
                    >
                      Declare EAP 1
                    </button>
                  )}
                  {c.actions.includes('defer-maintenance') && (
                    <button onClick={() => setView('powerhouse')}>Go to powerhouse</button>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>

        <div className="label" style={{ margin: '14px 0 6px' }}>
          Standing actions
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button disabled={snap.tdgWaiverRequested} onClick={() => dispatch({ t: 'request-tdg-waiver' })}>
            Request TDG waiver
          </button>
          <button disabled={snap.floodWarningAt !== null} onClick={() => dispatch({ t: 'issue-flood-warning' })}>
            Issue flood warning
          </button>
          <button onClick={() => dispatch({ t: 'dispatch-crew', target: 'spillway hoist' })}>Dispatch crew</button>
          <button
            className={snap.overrideActive ? 'armed' : undefined}
            onClick={() =>
              requestConfirm({
                label: snap.overrideActive ? 'Cancel licence override' : 'Override licence constraint',
                detail:
                  'While an override is active every penalty is doubled and the action is logged. Override to save the dam, not to save money.',
                confirm: () => dispatch({ t: 'override', active: !snap.overrideActive }),
              })
            }
          >
            {snap.overrideActive ? 'Override active' : 'Override licence'}
          </button>
        </div>

        <div className="num" style={{ marginTop: 12, color: 'var(--ink-dim)', fontSize: 12 }}>
          EAP level {snap.eapLevel} · environmental compliance {snap.envCompliancePct.toFixed(1)} % ·
          {snap.regulatoryMaxPool !== null && ` regulator cap ${snap.regulatoryMaxPool.toFixed(1)} m ·`}
          {snap.releaseFreezeMinutes > 0 && ` release freeze ${Math.ceil(snap.releaseFreezeMinutes)} min ·`} active
          events: {snap.activeEventIds.join(', ') || 'none'}
        </div>
        <div style={{ color: 'var(--ink-dim)', fontSize: 12, marginTop: 6 }}>
          Acknowledging means “I have seen it”, not “it is handled”. Unacknowledged Emergency and
          High alarms cost $1,000 a minute after ten minutes. Suppression is built in — one alarm
          per gate, per unit, per constraint — so {site.name} cannot bury you in forty rows.
        </div>
      </section>
    </div>
  );
}
